"use client";

import React from "react";
import { Building2, DollarSign, Rocket, Headset } from "lucide-react";
import { useEffect, useRef, useState } from "react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.1,
  rootMargin = "50px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const benefits = [{
  title: "Same Day Service",
  description: "Need it gone now? We offer rapid response times to clear your space on the very same day you call us.",
  icon: Rocket
}, {
  title: "Fully Insured",
  description: "Rest easy knowing our professional crews and your property are protected by comprehensive insurance coverage.",
  icon: Building2
}, {
  title: "Upfront Pricing",
  description: "No hidden fees or surprises. We provide clear, transparent quotes before any work begins on your project.",
  icon: DollarSign
}, {
  title: "Eco-Friendly Disposal",
  description: "We prioritize recycling and donating items to local charities to keep as much as possible out of Ottawa landfills.",
  icon: Headset
}];
export default function WhyChooseUs() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  return <section data-section-id="253902" id="why-choose-us" className="relative py-24 lg:py-32 bg-background text-foreground overflow-hidden">
      {}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-secondary/10 rounded-lg blur-[100px] pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div ref={headerRef} className={`text-center max-w-3xl mx-auto mb-16 lg:mb-24 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-default text-foreground mb-6 tracking-tight">
            Why Choose Us
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground font-default leading-relaxed">
            Ottawa's trusted local junk removal experts, committed to professional service and sustainable disposal practices.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 max-w-5xl mx-auto">
          {benefits.map((benefit, i) => <BenefitCard key={i} benefit={benefit} index={i} />)}
        </div>
      </div>
    </section>;
}
function BenefitCard({
  benefit,
  index
}: {
  benefit: (typeof benefits)[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const Icon = benefit.icon;
  return <div data-index={index} ref={ref} className={`group relative rounded-lg p-[1px] transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
    transitionDelay: `${index * 150}ms`
  }}>
      {}
      <div className="absolute inset-0 rounded-lg bg-gradient-to-b from-secondary/30 to-transparent group-hover:from-secondary/60 group-hover:to-secondary/20 transition-colors duration-500" />
      
      {}
      <div className="absolute inset-0 bg-secondary/20 blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-lg pointer-events-none" />

      {}
      <div className="relative h-full bg-card/60 backdrop-blur-xl rounded-lg p-8 md:p-10 flex flex-col gap-5 border border-border/50 group-hover:-translate-y-1 transition-transform duration-500 shadow-md">
        <div className="w-12 h-12 rounded-md bg-primary/10 flex items-center justify-center border border-primary/20 group-hover:border-secondary/40 group-hover:bg-secondary/20 transition-colors duration-500">
          <Icon className="w-6 h-6 text-primary group-hover:text-primary transition-colors" />
        </div>
        
        <div>
          <h3 className="text-xl md:text-2xl font-semibold font-default text-foreground mb-3 group-hover:text-primary transition-colors duration-300">
            {benefit.title}
          </h3>
          <p className="text-muted-foreground font-default leading-relaxed">
            {benefit.description}
          </p>
        </div>
      </div>
    </div>;
}